<template>
  <div class="item-container">
    <template v-if="printStatus === 'FAILED'">
      <h2 class="center">
        {{ $t('tcm.prescription') }}
        {{ $t('pharmacy.nothingMessage') }}
      </h2>
    </template>
    <template v-else-if="printStatus === 'PRINTED'">
      <div class="prescription" v-for="(prescription, index) in consultation.order" :key="index">
        <div class="prescription-container" v-if="hasMedicineToPrint(prescription)">
          <div class="row mb-3">
            <div class="col-9">
              <img v-if="featureFlags.isMaKuang" class="mb-4" style="width: 520px; margin-left: 0; height: 150px;" :src="generalConfig.logoURL">
              <img v-else class="mb-4" style="width: 300px; margin-left: 0;" :src="generalConfig.logoURL">
            </div>
          </div>
          <h3 class="mb-5 mt-3 text-center text-uppercase">
            {{ $t('general.prescriptionFormTitle') }} {{index + 1}}
          </h3>
          <div class="row mb-4">
            <div class="col-6">
              <div class="row mb-2">
                <div class="col-5 text-uppercase form-label">{{ $t('tcm.consultationPrescription.patientName') }}:</div>
                <div class="col-7">{{ consultation.patient.name }}</div>
              </div>
              <div class="row mb-2">
                <div class="col-5 text-uppercase form-label">{{ $t('tcm.consultationPrescription.patientIC') }}:</div>
                <div class="col-7">{{ consultation.patient.nric }}</div>
              </div>
              <div class="row mb-2">
                <div class="col-5 text-uppercase form-label">{{ $t('tcm.consultationPrescription.regNo') }}:</div>
                <div class="col-7">{{ `${consultation.patient.givenIdPrefix ? consultation.patient.givenIdPrefix : ''}${consultation.patient.givenId}` }}</div>
              </div>
              <div class="row mb-2">
                <div class="col-5 text-uppercase form-label">{{ $t('tcm.consultationPrescription.attendedBy') }}:</div>
                <div class="col-7">{{ showAttendedBy }}</div>
              </div>
              <div class="row mb-2">
                <div class="col-5 text-uppercase form-label">{{ $t('tcm.consultationPrescription.visitDate') }}:</div>
                <div class="col-7">{{ visitDate }}</div>
              </div>
              <div class="row mb-2">
                <br />
              </div>
              <div class="row mb-2">
                <div class="col-5 text-uppercase form-label">{{ $t('tcm.consultationPrescription.diagnosis') }}:</div>
                <div class="col-7">{{ consultation.diseaseName ? consultation.diseaseName : '' }}</div>
              </div>
              <div class="row mb-2">
                <div class="col-5 text-uppercase form-label">{{ $t('tcm.consultationPrescription.totalDays') }}:</div>
                <div class="col-7">{{ prescription.totalDays || '' }}</div>
              </div>
              <div class="row mb-2">
                <div class="col-5 text-uppercase form-label">{{ $t('tcm.consultationPrescription.ratio') }}:</div>
                <div class="col-7">{{ prescription.dilutionRatio || '' }}</div>
              </div>
              <div class="row mb-2">
                <div class="col-5 text-uppercase form-label">{{ $t('tcm.consultationPrescription.totalPackages') }}:</div>
                <div class="col-7">{{ prescription.totalPackages || '' }}</div>
              </div>
            </div>
            <div class="col-6">
              <div class="row mb-2"><br /></div>
              <div class="row mb-2"><br /></div>
              <div class="row mb-2">
                <div class="col-4 text-uppercase form-label">{{ $t('tcm.consultationPrescription.visitClinic') }}:</div>
                <div class="col-8">{{ consultation.clinic.name }}</div>
              </div>
              <div class="row mb-2">
                <div class="col-4 text-uppercase form-label">{{ $t('tcm.consultationPrescription.clinicAsst') }}:</div>
                <div class="col-8">{{ consultation.queue && consultation.queue.addedBy ? consultation.queue.addedBy.name : '' }}</div>
              </div>
              <div class="row mb-2">
                <div class="col-4 text-uppercase form-label">{{ $t('tcm.consultationPrescription.printDate') }}:</div>
                <div class="col-8"> {{ printDate }}</div>
              </div>
            </div>
          </div>
          <div class="mb-4">
            <div class="row med-header-en">
              <div class="col text-uppercase">{{ $t('tcm.consultationPrescription.code') }}</div>
              <div class="col text-uppercase">{{ $t('tcm.consultationPrescription.medicineChinese') }}</div>
              <div class="col text-uppercase">{{ $t('tcm.consultationPrescription.medicine') }}</div>
              <div class="col text-uppercase">{{ $t('tcm.consultationPrescription.location') }}</div>
              <div class="col text-uppercase" v-if="printWithDailyDosage">{{ $t('tcm.consultationPrescription.dailyDosage') }}</div>
              <div class="col text-uppercase" v-if="printWithDailyDosage">{{ $t('tcm.consultationPrescription.totalDosage') }}</div>
              <div class="col text-uppercase">{{ $t('tcm.consultationPrescription.note') }}</div>
            </div>
            <div class="row med-header-cn">
              <div class="col text-uppercase">{{ $t('tcm.consultationPrescription.code-cn') }}</div>
              <div class="col text-uppercase">{{ $t('tcm.consultationPrescription.medicineChinese-cn') }}</div>
              <div class="col text-uppercase">{{ $t('tcm.consultationPrescription.medicine-cn') }}</div>
              <div class="col text-uppercase">{{ $t('tcm.consultationPrescription.location-cn') }}</div>
              <div class="col text-uppercase" v-if="printWithDailyDosage">{{ $t('tcm.consultationPrescription.dailyDosage-cn') }}</div>
              <div class="col text-uppercase" v-if="printWithDailyDosage">{{ $t('tcm.consultationPrescription.totalDosage-cn') }}</div>
              <div class="col text-uppercase">{{ $t('tcm.consultationPrescription.note-cn') }}</div>
            </div>

            <div class="row med-body" v-for="item in medicinesToPrint(prescription)" :key="item._id">
              <div class="col">{{ item.givenId }}</div>
              <div class="col">{{ item.name }}</div>
              <div class="col">{{ item.chineseName || '' }}</div>
              <div class="col">{{ item.location || '' }}</div>
              <div class="col" v-if="printWithDailyDosage">{{ item.qty }} {{ item.unit }}</div>
              <div class="col" v-if="printWithDailyDosage">{{ calTotalDosage({ dosage: item.qty || 0, totalDays: prescription.totalDays || 1, ratio: prescription.dilutionRatio || 1 }) }} {{ item.unit }}</div>
              <div class="col">{{ item.remarks }}</div>
            </div>
          </div>
          <div class="mb-5 row">
            <div class="col-7 form-label"></div>
            <div class="col-5 form-label">
              <div class="row mb-2">
                <div class="col-5 text-uppercase form-label">{{ $t('tcm.consultationPrescription.totalDosage')
                }}:</div>
                <div class="col-7">{{ prescriptionTotalDosage(prescription) }} {{ prescriptionPrintUnit(prescription) }}</div>
              </div>
            </div>
          </div>
          <br/>
          <br/>
          <br/>
          <br/>
          <br/>
          <div class="mb-5 row med-footer">
            <div class="col-6 form-label"></div>
            <div class="col-6 form-label">
              <div class="row mb-2 signature-line">
              </div>
            </div>
            <div class="col-6 form-label"></div>
            <div class="col-6 form-label">
              <div class="row mb-2">
                <div class="col-7 text-uppercase form-label">{{ $t('tcm.consultationPrescription.signatureTcmPhysician')
                }}:</div>
                <div class="col-5">{{ showAttendedBy }}</div>
              </div>
            </div>
            <div class="col-6 form-label">
              <div class="footer-item  text-uppercase">
                {{ $t('tcm.consultationPrescription.instruction') }}:
              </div>
              <div class="footer-item">
                <div class="row mb-2">
                  <div class="col-4 text-uppercase form-label"> {{ $t('tcm.consultationPrescription.instruction-cn') }}:
                  </div>
                  <div class="col-8">{{ prescription.dosage }}</div>
                </div>
                <div class="row mb-2">
                  <div class="col-4 text-uppercase form-label"></div>
                  <div class="col-8">{{ prescription.remarks }}</div>
                </div>
              </div>
            </div>
            <div class="col-6 form-label">
            </div>
          </div>
          <div class="page-break" v-if="index !== (withItemsOrders.length - 1)"></div>
        </div>
      </div>
    </template>
  </div>
</template>
<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import moneyFormatter from '@/components/mixins/money';
import currency from 'currency.js'
import dateformatter from '@/components/mixins/dateformatter';
import constant from '@/services/constant';

const { PRINT_STATUS } = constant;
export default {
  name: 'TcmConsultationPrescriptionRecord',
  mixins: [moneyFormatter, dateformatter],
  props: {
    consultation: {
      type: Object,
      default: () => ({}),
    },
    printWithDailyDosage: {
      type: Boolean,
      default: false,
    },
    status: {
      type: String,
      default: PRINT_STATUS.PRINTING,
    },
  },
  data() {
    return {
      printDate: moment().format('YYYY/MM/DD'),
    };
  },
  computed: {
    ...mapGetters('config', {
      generalConfig: 'general',
      inventoryConfig: 'inventory',
      featureFlags: 'featureFlags',
    }),
    visitDate() {
      return this.consultation && this.formatDate(this.consultation.visitDate);
    },
    showAttendedBy() {
      return (this.consultation && this.consultation.doctor && this.consultation.doctor.name)
      || (this.consultation && this.consultation.queue && this.consultation.queue.therapist && this.consultation.queue.therapist.name);
    },
    printStatus() {
      return this.status;
    },
    withItemsOrders() {
      const result = this.consultation.order.filter(prescription => this.hasMedicineToPrint(prescription));
      return result;
    },
  },
  methods: {
    statusPrinted() {
      return PRINT_STATUS.PRINTED;
    },
    statusFailed() {
      return PRINT_STATUS.FAILED;
    },
    hasMedicineToPrint(prescription) {
      return prescription.items.filter(item => this.printPrescriptionCategories().includes(item.category)).length > 0;
    },
    medicinesToPrint(prescription) {
      const data = prescription.items
        .filter(item => this.printPrescriptionCategories().includes(item.category))
        .map(item => ({  // Use current location not the location the time prescription was created
          ...item,
          location: (item.id && item.id.location) || ''
        }));
      return data;
    },
    prescriptionTotalDosage(prescription) {
      const data = prescription.items.filter(item => this.printPrescriptionCategories().includes(item.category));
      if (data.length > 0) {
        return _.sumBy(data, item => this.calTotalDosage({ dosage: item.qty || 0, totalDays: prescription.totalDays || 1, ratio: prescription.dilutionRatio || 1 }));
      }
      return 0;
    },
    prescriptionPrintUnit(prescription) {
      const data = prescription.items.filter(item => this.printPrescriptionCategories().includes(item.category));
      if (data.length > 0) {
        return data[0].unit;
      }
      return '';
    },
    printPrescriptionCategories() {
      const { printPrescriptionCategories = '' } = this.inventoryConfig;
      return printPrescriptionCategories.split(',').filter(category => typeof category === 'string' && category.length !== 0);
    },
    calTotalDosage({ dosage, totalDays, ratio }) {
      return currency(dosage).multiply(totalDays).multiply(ratio / totalDays).value
    },
  },
};
</script>
<style lang="scss" scoped>
.classified-medicine-tip {
  font-size: 20px;
  font-weight: bold;
  margin-top: 40px;
}
.clinic-name {
  margin-top: 10px;
}
.clinic-address {
  font-weight: bold;
  margin-top: 10px;
}
.form-label {
  font-weight: bold;
}
.medicine-content-title {
  font-size: 20px;
  font-weight: bold;
}
.med-header-en {
  font-weight: bold;
  padding: 10px 0;
  border-width: 2px 0 0 0;
  border-style: solid;
  border-color: #333;
  margin-left: 0;
  margin-right: 0;
}
.signature-line {
  font-weight: bold;
  padding: 10px 0;
  border-width: 2px 0 0 0;
  border-style: solid;
  border-color: #333;
  margin-left: 0;
  margin-right: 15%;
  padding: 0;
}
.med-header-cn {
  font-weight: bold;
  padding: 10px 0;
  border-width: 0 0 2px 0;
  border-style: solid;
  border-color: #333;
  margin-left: 0;
  margin-right: 0;
}
.med-body {
  padding: 10px 0;
  border-bottom: 1px solid #333;
  margin-left: 0;
  margin-right: 0;
}
.dividing-wrap {
  font-weight: bold;
  padding-top: 10px;
  border-top: 2px solid #333;
  margin-top: 30px;
}
.page-break {
  page-break-after: always;
}
.med-footer {
  display: flex;
  align-items: flex-end;
}
</style>
